import React, { useCallback, useContext, useId, useMemo, useRef, useState } from 'react'
import OfferCapacityWithAgesSelectBox from './OfferCapacityWithAgesSelectBox'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import Dropdown from 'components/Luxkit/Dropdown/v2/Dropdown'
import DropdownContainer from 'components/Luxkit/Dropdown/DropdownContainer'
import { Placement } from '@floating-ui/react-dom'
import { useIsMobileScreen } from 'lib/deviceUtils'
import OfferCapacityWithAgesVerticalSelection from './OfferCapacityWithAgesVerticalSelection'
import { generateRoomOccupants, getMaxGuestCount } from 'lib/offer/occupancyUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import OfferBookButton from 'components/OfferPage/OfferBookButton'
import { canBookNow, getPackagesForFilters } from 'selectors/offerPageSelectors'
import TextButton from 'components/Luxkit/Button/TextButton'
import { useElementWidth } from 'hooks/useElementSize'

interface Props {
  open: boolean;
  capacities?: Array<App.PackageRoomCapacity>;
  occupantUnit?: 'guest' | 'traveller';
  childrenAgeLabel?: string
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  dropdownPosition?: Placement;
  anchorRef?: React.RefObject<HTMLElement>;
  disableMultiRoom?: boolean;
  canBookNow?: boolean;
  relevantPackage: App.Package;
  rooms?: Array<App.RoomOccupants>;
}

const defaultRooms = [generateRoomOccupants()]

function OfferCapacityWithAgesVerticalSelect(props: Props) {
  const {
    open,
    capacities,
    occupantUnit = 'guest',
    childrenAgeLabel = 'at time of check-in',
    disableMultiRoom,
    onClick,
    dropdownPosition,
    anchorRef,
    canBookNow,
    relevantPackage,
    rooms = defaultRooms,
  } = props
  const triggerRef = useRef(null)
  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const isMobile = useIsMobileScreen()
  const [localRooms, setLocalRooms] = useState<Array<App.RoomOccupants | App.Occupants>>(rooms)

  const saveRooms = useCallback(() => {
    dispatch({
      type: OfferPageActions.setRooms,
      rooms: localRooms as any,
    })
  }, [dispatch, localRooms])

  const onClose = useCallback(() => {
    saveRooms()
    dispatch({ type: OfferPageActions.closeFilter })
    trackEvent('OCCUPANCY_FILTERS_CLOSED')
  }, [dispatch, saveRooms, trackEvent])

  const onContinue = useCallback(() => {
    saveRooms()
    dispatch({ type: OfferPageActions.filterContinue })
  }, [dispatch, saveRooms])

  const maxGuests = useMemo((): number => {
    if (capacities) {
      return getMaxGuestCount(capacities)
    }
    return 0
  }, [capacities])

  const onDropdownSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onContinue()
  }, [onContinue])

  const formId = useId()
  const dropdownWidth = useElementWidth(anchorRef)

  return (
    <>
      <OfferCapacityWithAgesSelectBox
        ref={triggerRef}
        occupantUnit={occupantUnit}
        onClick={onClick}
        multiRoomDisabled={disableMultiRoom}
      />
      <Dropdown
        open={open && !isMobile}
        onClose={onClose}
        triggerRef={triggerRef}
        anchorRef={anchorRef}
        data-testid="OfferCapacitySelect"
        disableFlip
        position={dropdownPosition}
        width={dropdownWidth}
      >
        <DropdownContainer
          title="Who is going?"
          subtitle={maxGuests > 0 ? `Maximum occupancy is ${pluralizeToString(occupantUnit, maxGuests)}` : undefined}
          onClose={onClose}
          fit="auto"
          footer={<>
            {canBookNow && <OfferBookButton
              fit="full-width"
              size="large"
              type="submit"
              form={formId}
              pkg={relevantPackage}
              overrideRooms={localRooms}
            >
              Book now
            </OfferBookButton>}
            {!canBookNow && <TextButton
              kind="primary"
              fit="full-width"
              size="large"
              type="submit"
              form={formId}
             >
              Continue
            </TextButton>}
          </>}
        >
          <form onSubmit={onDropdownSubmit} id={formId}>
            <OfferCapacityWithAgesVerticalSelection
              capacities={capacities}
              childrenAgeLabel={childrenAgeLabel}
              disableMultiRoom={disableMultiRoom}
              onChange={setLocalRooms}
            />
          </form>
        </DropdownContainer>
      </Dropdown>
    </>
  )
}

export default withOfferPageAndReduxState(
  (state, appState) => ({
    canBookNow: canBookNow(state, appState),
    relevantPackage: getPackagesForFilters(appState, state)[0]?.package,
    open: state.activeFilter === 'capacity',
    rooms: state.rooms,
  }))(OfferCapacityWithAgesVerticalSelect)
